import React, {useCallback} from 'react';
import {Referral, specialReferralConsiderations} from '../domains';
import {
    ProviderRequestColumn,
    OpenReferralColumn,
    CompletedReferralsColumn,
    MatchedReferralsColumn
} from './ReferralList';
import {ReferralPageAction} from 'placement/reducers/referralPage';
import {ReferralToolTip} from './ReferralTooltip';
import {PAGE, REFERRAL_STATUS} from 'placement/const';
import {useGoToReferralDetailPage} from 'placement/routes';
import {isValidDate, formatDate} from 'common/util/date';
import {ReferralCommunicationsPopup} from './ReferralCommunicationsPopup';
import {useFeatureFlagFilter} from 'featureflag';

type CasePreviewProps = {
    referral: Referral;
    selected: boolean;
    page: PAGE;
    dispatch: React.Dispatch<ReferralPageAction>;
};

export function ReferralPreview({referral, page, dispatch, selected}: CasePreviewProps) {
    const goToReferralDetailPage = useGoToReferralDetailPage({
        referralGuid: referral.guid,
        siblingReferralGuids: referral.clientSiblingReferralsAllProvider
            .filter((sibling) => sibling.siblingReferralGuid != null)
            .map((sibling) => sibling.siblingReferralGuid),
    });

    const onClick = useCallback(
        () => {
            dispatch({type: 'SET_SHOW_MODAL', selectedReferral: {...referral}})
            if (page === PAGE.PROVIDER_REQUEST ||
                page === PAGE.COMPLETE_REFERRALS ||
                page === PAGE.MATCHED_REFERRALS) {
                goToReferralDetailPage();
            }
        },
        [dispatch, goToReferralDetailPage, page, referral]
    );

    let selectedProviderName = '';
    if (referral.matchedOffer) {
        selectedProviderName = referral.matchedOffer.provider.name;
    } else if (referral.providerSelected) {
        selectedProviderName = referral.providerSelected;
    }

    let columnsMapper = ProviderRequestColumn;
    switch (page) {
        case PAGE.PROVIDER_REQUEST:
            columnsMapper = ProviderRequestColumn;
            break;
        case PAGE.MATCHED_REFERRALS:
            columnsMapper = MatchedReferralsColumn;
            break;
        case PAGE.OPEN_REFERRALS:
            columnsMapper = OpenReferralColumn;
            break;
        case PAGE.COMPLETE_REFERRALS:
            columnsMapper = CompletedReferralsColumn;
            break;
    }
    const visibleColumns = useFeatureFlagFilter(columnsMapper);

    return (
        <tr className={`border-b border-gray-200 hover:${selected ? 'bg-botticelli-100' : 'bg-gray-200'} ${selected ? 'bg-botticelli-100 text-white' : ''}`}
            onClick={() => onClick()}>
            {
                visibleColumns.map((column) => (
                    <td
                        key={column.name}
                        className={
                            'py-5'
                            + (column.attr === 'caseNumber' ? ' pl-12' : '')
                            + (column.attr === 'serviceTypes' ? ' max-w-xs truncate' : '')
                        }>
                        {
                            column.attr === 'caseNumber' &&
                            <div className='absolute -ml-10 text-yellow-500'>
                                <ReferralToolTip
                                    specialConsiderations={specialReferralConsiderations(referral)}
                                    page={page}
                                    referralStatus={referral.status as REFERRAL_STATUS}/>
                            </div>
                        }
                        {column.attr === 'offerConversationSummaries' &&
                            (page === PAGE.PROVIDER_REQUEST || (referral.offerConversationSummaries?.length ?? 0) > 0) && (
                                <div className="flex justify-center" onClick={(event) => event.stopPropagation()}>
                                    <ReferralCommunicationsPopup
                                        referralGuid={referral.guid}
                                        summaries={referral.offerConversationSummaries ?? []}
                                        readOnly={page !== PAGE.PROVIDER_REQUEST}
                                    />
                                </div>
                            )}
                        <span>{column.attr === 'matchedOfferProviderName' && selectedProviderName}</span>
                        <span>
                            {
                                ['matchedDate', 'completeDate', 'clientNotifiedDate'].includes(column.attr) &&
                                referral[column.attr] && isValidDate(new Date(referral[column.attr] as number)) &&
                                formatDate(new Date(referral[column.attr] as number))
                            }
                        </span>
                        {
                            !['matchedDate', 'completeDate', 'clientNotifiedDate', 'matchedOfferProviderName', 'offerConversationSummaries'].includes(column.attr) &&
                            <span>{Array.isArray(referral[column.attr]) ? (referral[column.attr] as Array<String>).sort().join(', ') : referral[column.attr]}</span>
                        }
                    </td>
                ))
            }
        </tr>
    );
}
